import React, { useEffect, useState } from 'react';
import { usePathname, useRouter, useSearchParams } from 'next/navigation';
import { XMarkIcon } from '@heroicons/react/24/outline';
import { Wishlist as WishlistData } from 'shared/types/wishlist';
import Button from 'components/commercetools-ui/atoms/button';
import useButtonClassNames from 'components/commercetools-ui/atoms/button/useButtonClassNames';
import Input from 'components/commercetools-ui/atoms/input';
import Message from 'components/commercetools-ui/atoms/message';
import Modal from 'components/commercetools-ui/atoms/modal';
import Typography from 'components/commercetools-ui/atoms/typography';
import { default as MailIcon } from 'components/icons/mail';
import { useFormat } from 'helpers/hooks/useFormat';
import aggregatedQuantity from 'helpers/utils/aggregatedQuantity';
import { useAccount, useStock, useWishlist } from 'frontastic';
import { ImageProps } from 'frontastic/lib/image';
import WishlistItem, { TWishlistItem } from './components/wishlist-item';
import WishlistSkeleton from './components/wishlist-skeleton';
import { Link } from '../header/types';

export interface WishlistProps {
  pageTitle?: string;
  emptyWishlistTitle: string;
  emptyWishlistSubtitle: string;
  emptyWishlistImage: ImageProps;
  productLogo?: ImageProps;
  emptyWishlistCategories: Link[];
  handleCategoryClick?: () => void;
  isPage?: boolean; // this indicates whether this component rendered in a page or modal
}
const Wishlist: React.FC<WishlistProps> = ({ isPage = false, productLogo }) => {
  const buttonClassNames = useButtonClassNames({ variant: 'primary', size: 'm', includesIcon: false });
  const { account } = useAccount();
  const [recipients, setRecipients] = useState<string>('');
  const [isOpen, setIsOpen] = useState(false);
  const pathname = usePathname();
  const params = useSearchParams();
  const [isShared, setIsShared] = useState<boolean | null>(null);
  const [baseUrl, setBaseUrl] = useState('');
  const { formatMessage: formatWishlistMessage } = useFormat({ name: 'wishlist' });
  const { fluentStocks, triggerGetStock, itemsWithStockStatus } = useStock();
  const { data, deleteWishlist, wishlistProductSKUs, getWishlist, totalItems: count, hasExcludedItem } = useWishlist();
  const [wishlistData, setWishlistData] = useState<WishlistData | undefined>(data);
  const [totalItems, setTotalItems] = useState<number | undefined>(count);
  const [isLoading, setIsLoading] = useState(true);
  const [hasSoldOutItem, setHasSoldOutItem] = useState(false);

  useEffect(() => {
    if (data && fluentStocks.length !== 0) {
      const soldOut = data?.lineItems?.some((item) => {
        if (item?.isPerpetualStock) {
          return false;
        }

        const availableQuantity = aggregatedQuantity(item.variant?.sku, fluentStocks);
        return availableQuantity === 0;
      });

      setHasSoldOutItem(soldOut as boolean);
    }
  }, [data, fluentStocks]);

  const { push } = useRouter();

  useEffect(() => {
    const wishlistID = params.get('wishlistId');
    const isShared = !!wishlistID;
    if (!account && !isShared) {
      push('/login');
    }

    setTimeout(() => {
      if (isLoading) {
        setIsLoading(!isLoading);
      }
    }, 3000);
  }, []);

  const handleDeleteWishlist = async () => {
    if (wishlistData) await deleteWishlist(wishlistData);
  };

  triggerGetStock(wishlistProductSKUs as string[]); // fetch realtime inventory of stocks from Fluent OMS using CT SKUs

  const wishListDataWithStocks = itemsWithStockStatus(wishlistData as WishlistData);

  const onChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setRecipients(e.target.value);
  };

  const handleOpen = () => {
    setRecipients('');
    setIsOpen(true);
  };

  const handleClose = () => {
    setIsOpen(false);
  };

  useEffect(() => {
    if (typeof window !== 'undefined') {
      setBaseUrl(`${window.location.origin}${pathname}`);
    }
  }, [pathname]);

  useEffect(() => {
    if (!isShared) {
      setWishlistData(data);
      setTotalItems(count);
    }
  }, [data, isShared]);

  useEffect(() => {
    const getWishlistData = async () => {
      if (params.get('wishlistId')) {
        const res = await getWishlist(params.get('wishlistId') as string);
        setIsShared(true);
        setTotalItems(res.data?.lineItems?.length !== 0 ? res.data?.lineItems?.length : 0);
        setWishlistData(res.data);
      } else {
        setIsShared(false);
      }
    };
    getWishlistData();
  }, [isShared, params, getWishlist]);

  return (
    <>
      {isPage && (
        <div className="relative bg-neutral-200">
          {!wishListDataWithStocks && isLoading ? (
            <WishlistSkeleton />
          ) : (
            <div className="flex flex-col lg:flex-row lg:items-start lg:gap-26 lg:px-20 lg:py-48 xl:px-48">
              {/* wishlist content */}
              <div className="bg-white px-16 py-12 md:px-24 md:py-12 lg:w-full lg:rounded-md lg:px-20 lg:py-36 xl:px-48">
                <Modal
                  isOpen={isOpen}
                  style={{ content: { width: 400, height: 280, overflow: 'hidden', background: 'white' } }}
                >
                  <>
                    <XMarkIcon
                      className="absolute right-15 top-15 h-24 w-24 hover:cursor-pointer"
                      strokeWidth={1}
                      color="#494949"
                      onClick={handleClose}
                    />
                    <div className="m-auto grid h-full content-center gap-24 px-24">
                      <div className="flex flex-col gap-8">
                        <Typography as="h3" className="text-20 font-medium text-primary-black">
                          {formatWishlistMessage({ id: 'add.recipients', defaultMessage: 'Add recipients' })}
                        </Typography>
                        <Input
                          onChange={onChange}
                          value={recipients}
                          type="email"
                          className="w-full"
                          placeholder="e.g., friend@example.com, family@example.com"
                          multiple
                        />

                        <p className="text-sm font-normal text-gray-600">
                          Note: Enter email addresses, separated by commas, to share your wishlist
                        </p>
                      </div>
                      <a
                        href={`mailto:?to=${recipients} &subject=My Wishlist &body=${baseUrl}?wishlistId=${wishlistData?.wishlistId}`}
                        onClick={handleClose}
                        className={`${buttonClassNames} text-center`}
                      >
                        {formatWishlistMessage({ id: 'send.email', defaultMessage: 'Send email' })}
                      </a>
                    </div>
                  </>
                </Modal>
                <div className="container mx-auto px-4 py-8">
                  <h1 className="m-0 font-heading text-[23px] uppercase leading-[25px] md:w-full md:text-[34px] md:leading-[40px] lg:text-[28px]  xl:text-[34px]">
                    {formatWishlistMessage({ id: 'my.wishlist', defaultMessage: 'My Wishlist' })}
                    {totalItems !== 0 && ': '}
                    {totalItems !== 0 && (
                      <span className="text-secondary-black">
                        ({totalItems} {formatWishlistMessage({ id: 'my.wishlist.items', defaultMessage: 'Items' })})
                      </span>
                    )}
                  </h1>

                  <div className="mt-12 divide-y divide-neutral-400 border-t border-neutral-400 lg:mt-34 lg:border-none">
                    {hasExcludedItem && (
                      <Message
                        severity={'error'}
                        message={formatWishlistMessage({
                          id: 'product.excluded',
                          defaultMessage:
                            "Unfortunately, your wishlist contains items which can't be fulfilled.  Please remove these items from your wishlist to continue.",
                        })}
                      ></Message>
                    )}
                    {wishListDataWithStocks?.map((lineItem) => (
                      <WishlistItem
                        key={lineItem.lineItemId}
                        isShared={isShared}
                        productLogo={productLogo}
                        item={lineItem as TWishlistItem}
                        isPage={isPage}
                        fluentStocks={fluentStocks}
                      />
                    ))}

                    {totalItems === 0 && (
                      <div className="mt-28 min-h-[306px]">
                        <p>
                          {formatWishlistMessage({
                            id: 'wishlist.empty',
                            defaultMessage: 'You currently have no items in your wish list.',
                          })}
                        </p>
                      </div>
                    )}
                  </div>
                  {totalItems !== 0 &&
                    (isShared == false ? (
                      <div className="flex items-center justify-center gap-8 leading-4 md:justify-end">
                        <p>Share my wishlist: </p>
                        <div className="flex h-32 w-32 items-center justify-center rounded-full bg-[#15164D]">
                          <Button variant="ghost" onClick={handleOpen} size="fit">
                            <MailIcon />
                          </Button>
                        </div>
                      </div>
                    ) : null)}
                </div>
                {totalItems !== 0 &&
                  (isShared == false ? (
                    <div className="flex w-full items-center justify-center">
                      <div className="my-20">
                        <Button onClick={handleDeleteWishlist} variant="secondary" className="w-full text-16">
                          {formatWishlistMessage({ id: 'wishlist.clear.list', defaultMessage: 'Clear the list' })}
                        </Button>
                      </div>
                    </div>
                  ) : null)}
              </div>
            </div>
          )}
        </div>
      )}

      {!isPage && (
        <>
          <div className="h-[83vh] grow divide-y divide-neutral-400 overflow-auto px-12 md:px-22">
            {wishListDataWithStocks?.map((lineItem) => (
              <WishlistItem
                key={lineItem.lineItemId}
                isShared={isShared}
                productLogo={productLogo}
                item={lineItem as TWishlistItem}
                isPage={isPage}
                fluentStocks={fluentStocks}
              />
            ))}
            {totalItems !== 0 &&
              (isShared == false ? (
                <div className="flex items-center justify-center gap-8 leading-4 md:justify-end">
                  <p>Share my wishlist: </p>
                  <div className="flex h-32 w-32 items-center justify-center rounded-full bg-[#15164D]">
                    <Button variant="ghost" onClick={handleOpen} size="fit">
                      <MailIcon />
                    </Button>
                  </div>
                </div>
              ) : null)}
          </div>
          {isShared == false ? (
            <div className="absolute bottom-0 h-88 w-full p-20">
              <Button onClick={handleDeleteWishlist} variant="secondary" className="w-full text-16">
                {formatWishlistMessage({ id: 'wishlist.clear.list', defaultMessage: 'Clear the list' })}
              </Button>
            </div>
          ) : null}
        </>
      )}
    </>
  );
};
export default Wishlist;
